import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import { BaseCellComponent } from '../base-cell.component';
import { toContextArray, toContextArrayOrEmpty, firstContextRecord } from 'src/app/common/bhub-common-module/utils/context-array.util';
import { DatasetService } from 'src/app/services/dataset.service';
import { DynamicTableService } from 'src/app/services/dynamic-table.service';
import { Dataset, DataTableSchema, DataSchemaDataType } from 'src/app/services/models/datasets.models';
import { ViewTab, ViewManager, ColumnFieldSetting, ColumnCalcOperand, CalcConstraint, AddRowButton, CalcConditionalRule, CalcConditionClause } from 'src/app/board/pages/admin/admin-components/custom-views-config/view-builder/models/view-builder.models';
import { CellFilterService } from 'src/app/common/bhub-common-module/services/cell-filter.service';
import { Subject, takeUntil } from 'rxjs';

interface TableColumn {
  fieldName: string;
  displayName: string;
  dataType: string;
}

interface DropdownOption {
  value: any;
  label: string;
}

@Component({
  selector: 'bhub-data-entry-table-cell',
  standalone: false,
  templateUrl: './data-entry-table-cell.component.html',
  styleUrl: './data-entry-table-cell.component.scss'
})
export class DataEntryTableCellComponent extends BaseCellComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('tableHeader', { static: false }) tableHeader!: ElementRef<HTMLDivElement>;
  @ViewChild('tableBody', { static: false }) tableBody!: ElementRef<HTMLDivElement>;
  @Input() viewManager: ViewManager | null = null;
  @Input() availableTabs: ViewTab[] = [];
  @Output() contextDataChange = new EventEmitter<{[contextKey: string]: any}>();

  columns: TableColumn[] = [];
  displayedColumns: string[] = [];
  rows: any[] = [];
  private deletedRows: any[] = [];
  private datasetSchemas: DataTableSchema[] = [];
  private destroy$ = new Subject<void>();
  private currentDatasetId: number | null = null;

  // Dropdown options per column
  columnOptions: { [fieldName: string]: DropdownOption[] } = {};

  // Sorting
  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | null = null;

  isLoadingData = false;

  constructor(
    private datasetService: DatasetService,
    private dynamicTableService: DynamicTableService,
    private cellFilterService: CellFilterService
  ) {
    super();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    if (this.tableBody && this.tableHeader) {
      this.tableBody.nativeElement.addEventListener('scroll', () => {
        this.tableHeader.nativeElement.scrollLeft = this.tableBody.nativeElement.scrollLeft;
      });
    }
  }

  protected initializeCell(): void {
    this.registerContextVariable();
    this.setupFromDataset();
  }

  protected onCellOrDataChange(): void {
    this.setupFromDataset();
  }

  protected onCellUpdate(): void {
    this.setupFromDataset();
  }

  protected onDataSet(data: any): void {}

  // Reload when context changes (e.g., different inventory selected, tab switch).
  // Loads from context data which preserves local edits when just switching tabs,
  // but picks up new data when the dependency context changes.
  reloadData(): void {
    this.loadRowsFromContext();
  }

  // Re-fetch from backend after a successful save to get fresh rows with correct primary keys.
  // Called by action cells via refreshCells after save completes.
  reloadFromBackend(): void {
    this.deletedRows = [];
    const datasetId = this.cell?.dataBinding?.datasetId;
    if (datasetId) {
      this.loadTableData(datasetId);
    } else {
      this.loadRowsFromContext();
    }
  }

  private setupFromDataset(): void {
    const datasetId = this.cell?.dataBinding?.datasetId;
    if (!datasetId || datasetId === this.currentDatasetId) {
      if (this.columns.length > 0) {
        this.loadRowsFromContext();
      }
      return;
    }
    this.currentDatasetId = datasetId;

    this.datasetService.getDatasetReferences()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (datasetsMap: Map<number, Dataset>) => {
          const dataset = datasetsMap.get(datasetId);
          if (dataset) {
            this.datasetSchemas = dataset.dataTableSchemas;
            this.setupColumns();
            this.loadDropdownOptions(datasetsMap);

            // Check if the filter context has changed since we last loaded data.
            // If filters are the same and context has data, reuse it (preserves edits).
            // If filters changed (new inventory selected), re-fetch from backend.
            const currentFilterSig = this.getFilterSignature();
            const lastFilterSig = this.cell?.configuration?._lastFilterSignature;
            const contextVariable = this.contextVariable;
            const existingData = contextVariable && this.viewManager?.contextData?.[contextVariable];

            if (this.isContextSourced) {
              // Table is bound to an existing list context — never fetch from the dataset.
              // The context already holds the rows we should be editing.
              this.loadRowsFromContext();
            } else if (lastFilterSig && lastFilterSig === currentFilterSig
                && Array.isArray(existingData) && existingData.length > 0) {
              this.loadRowsFromContext();
            } else {
              this.loadTableData(datasetId);
            }
          }
        }
      });
  }

  private loadTableData(datasetId: number): void {
    // If configured filters can't resolve (e.g., the context value they reference is null),
    // don't make the request — show empty rather than unfiltered data
    if (this.cellFilterService.hasUnresolvedFilters(this.cell?.dataBinding?.cellFilters, this.viewManager)) {
      this.rows = [];
      this.isLoadingData = false;
      return;
    }

    this.isLoadingData = true;

    const cellFilters = this.cellFilterService.buildCellFilters(
      this.cell?.dataBinding?.cellFilters,
      this.viewManager
    );

    const limit = this.getConfigValue('dataLoadLimit', 100) as number;

    this.dynamicTableService.selectEntries(datasetId, limit, undefined, cellFilters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.rows = this.stringifyRowData(response.rowData || []);
          this.isLoadingData = false;
          // Save the filter signature so we know when filters change
          if (this.cell?.configuration) {
            this.cell.configuration._lastFilterSignature = this.getFilterSignature();
          }
          this.emitRows();
        },
        error: () => {
          this.rows = [];
          this.isLoadingData = false;
        }
      });
  }

  private getFilterSignature(): string {
    const filters = this.cellFilterService.buildCellFilters(
      this.cell?.dataBinding?.cellFilters,
      this.viewManager
    );
    return JSON.stringify(filters || {});
  }

  private stringifyRowData(rowData: any[]): any[] {
    return rowData.map(row => {
      const stringRow: any = {};
      Object.keys(row).forEach(key => {
        const val = row[key];
        stringRow[key] = val !== null && val !== undefined ? val : null;
      });
      return stringRow;
    });
  }

  private loadDropdownOptions(datasetsMap: Map<number, Dataset>): void {
    const columnSettings = this.getConfigValue('columnSettings', {}) as { [fieldName: string]: ColumnFieldSetting };
    if (!columnSettings) return;

    Object.entries(columnSettings).forEach(([fieldName, setting]) => {
      if (setting.inputType === 'static' && setting.staticOptions) {
        this.columnOptions[fieldName] = setting.staticOptions.map(opt => ({
          value: opt.value,
          label: opt.label || opt.value
        }));
      } else if (setting.inputType === 'dataset' && setting.optionsDatasetId && setting.optionsDisplayField && setting.optionsValueField) {
        this.dynamicTableService.selectEntries(setting.optionsDatasetId, -1)
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: (response) => {
              this.columnOptions[fieldName] = (response.rowData || []).map(row => ({
                value: row[setting.optionsValueField!],
                label: row[setting.optionsDisplayField!] != null ? String(row[setting.optionsDisplayField!]) : ''
              }));
            }
          });
      }
    });
  }

  getColumnSetting(fieldName: string): ColumnFieldSetting | null {
    const settings = this.getConfigValue('columnSettings', {}) as { [fieldName: string]: ColumnFieldSetting };
    return settings?.[fieldName] || null;
  }

  isDropdownColumn(column: TableColumn): boolean {
    const setting = this.getColumnSetting(column.fieldName);
    return (setting?.inputType === 'dataset' || setting?.inputType === 'static') && !!this.columnOptions[column.fieldName];
  }

  isCalculatedColumn(column: TableColumn): boolean {
    const setting = this.getColumnSetting(column.fieldName);
    return setting?.inputType === 'calculated';
  }

  getDropdownOptions(fieldName: string): DropdownOption[] {
    return this.columnOptions[fieldName] || [];
  }

  getCalculatedValue(row: any, column: TableColumn): string {
    const setting = this.getColumnSetting(column.fieldName);
    if (!setting) return '';

    const operands = this.resolveCalcOperands(setting, row);
    if (!operands || operands.length === 0) return '';

    let result: number | null = null;

    for (const operand of operands) {
      let numValue: number | null = null;

      switch (operand.sourceType) {
        case 'value':
          numValue = operand.literalValue ?? null;
          break;
        case 'row_field':
          numValue = this.parseNumeric(row?.[operand.rowFieldName!]);
          break;
        case 'context_field':
          numValue = this.parseNumeric(this.getContextFieldValue(operand.contextVariable!, operand.fieldName!));
          break;
        case 'context_lookup':
          numValue = this.parseNumeric(this.getContextLookupValue(operand, row));
          break;
      }

      if (numValue === null) continue;

      if (result === null) {
        result = operand.operator === '-' ? -numValue : numValue;
      } else {
        switch (operand.operator) {
          case '-': result -= numValue; break;
          case '*': result *= numValue; break;
          case '/': result = numValue !== 0 ? result / numValue : result; break;
          default:  result += numValue; break;
        }
      }
    }

    if (result === null) return '';

    // Apply constraints
    const constraints: CalcConstraint[] = setting.calcConstraints || [];
    result = this.applyConstraints(result, constraints);

    // Store the calculated value back on the row so it's included when saving
    row[column.fieldName] = result;

    const format = setting.calcResultFormat || 'number';
    const decimals = setting.calcDecimalPlaces ?? 2;
    return this.formatCalcValue(result, format, decimals);
  }

  private resolveCalcOperands(setting: ColumnFieldSetting, row: any): ColumnCalcOperand[] {
    if (!setting.calcUseConditional) {
      return setting.calcOperands || [];
    }

    // New multi-clause rule shape takes precedence when present.
    if (setting.calcConditionalRules && setting.calcConditionalRules.length > 0) {
      for (const rule of setting.calcConditionalRules) {
        if (this.allClausesPass(rule.clauses, row)) {
          return rule.operands || [];
        }
      }
      return setting.calcDefaultOperands || [];
    }

    // Legacy single-field-equality fallback — kept so old saved views still evaluate
    // without being forced through a re-save.
    const conditionField = setting.calcConditionRowField;
    if (!conditionField || !setting.calcConditions?.length) {
      return setting.calcOperands || [];
    }

    const conditionValue = String(row?.[conditionField] ?? '').trim().toLowerCase();
    const match = setting.calcConditions.find(c => c.conditionValue.trim().toLowerCase() === conditionValue);
    return match ? match.operands : [];
  }

  private allClausesPass(clauses: CalcConditionClause[] | undefined, row: any): boolean {
    if (!clauses || clauses.length === 0) return false; // empty rule never matches
    return clauses.every(clause => this.evaluateClause(clause, row));
  }

  private evaluateClause(clause: CalcConditionClause, row: any): boolean {
    // Reject incomplete clauses up front so a half-configured rule doesn't
    // falsely match every row (empty left field → '' compared against empty value → true).
    let leftRaw: any;
    if (clause.leftSource === 'context_field') {
      if (!clause.leftContextVariable || !clause.leftFieldName) return false;
      leftRaw = this.getContextFieldValue(clause.leftContextVariable, clause.leftFieldName);
    } else if (clause.leftSource === 'row_field') {
      if (!clause.leftRowField) return false;
      leftRaw = row?.[clause.leftRowField];
    } else {
      return false;
    }

    if (clause.compareNullOrEmpty) {
      const isEmpty = leftRaw === null || leftRaw === undefined || String(leftRaw).trim() === '';
      return clause.comparator === 'equals' ? isEmpty : !isEmpty;
    }

    const leftNorm = String(leftRaw ?? '').trim().toLowerCase();
    const rightNorm = String(clause.compareValue ?? '').trim().toLowerCase();
    return clause.comparator === 'equals' ? leftNorm === rightNorm : leftNorm !== rightNorm;
  }

  private getContextFieldValue(contextVariable: string, fieldName: string): any {
    if (!this.viewManager?.contextData) return null;
    const ctx = this.viewManager.contextData[contextVariable];
    if (!ctx) return null;
    const record = firstContextRecord(ctx);
    return record ? record[fieldName] : null;
  }

  private getContextLookupValue(operand: ColumnCalcOperand, row: any): any {
    if (!this.viewManager?.contextData || !operand.lookupContextVariable || !operand.lookupMatchField || !operand.lookupMatchRowField || !operand.lookupReturnField) {
      return null;
    }
    const ctx = this.viewManager.contextData[operand.lookupContextVariable];
    const records = toContextArray(ctx);
    if (!records) return null;
    const matchValue = String(row?.[operand.lookupMatchRowField] ?? '').trim().toLowerCase();
    const found = records.find((item: any) => String(item[operand.lookupMatchField!] ?? '').trim().toLowerCase() === matchValue);
    return found ? found[operand.lookupReturnField] : null;
  }

  private parseNumeric(raw: any): number | null {
    if (raw === null || raw === undefined) return null;
    if (typeof raw === 'number') return isNaN(raw) ? null : raw;
    if (typeof raw === 'string') {
      const cleaned = raw.replace(/[$€£¥,\s]/g, '');
      if (cleaned === '') return null;
      const num = Number(cleaned);
      return isNaN(num) ? null : num;
    }
    return null;
  }

  private applyConstraints(value: number, constraints: CalcConstraint[]): number {
    if (!constraints || constraints.length === 0) return value;
    for (const c of constraints) {
      switch (c.type) {
        case 'min':
          if (c.value !== undefined) value = Math.max(value, c.value);
          break;
        case 'max':
          if (c.value !== undefined) value = Math.min(value, c.value);
          break;
        case 'abs':
          value = Math.abs(value);
          break;
        case 'round':
          switch (c.roundMode) {
            case 'up': value = Math.ceil(value); break;
            case 'down': value = Math.floor(value); break;
            default: value = Math.round(value); break;
          }
          break;
      }
    }
    return value;
  }

  private formatCalcValue(value: number, format: string, decimals: number): string {
    switch (format) {
      case 'currency':
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
      case 'percent':
        return new Intl.NumberFormat('en-US', { style: 'percent', minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
      default:
        return new Intl.NumberFormat('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(value);
    }
  }

  private setupColumns(): void {
    this.columns = this.datasetSchemas
      .filter(schema => !schema.isHidden)
      .sort((a, b) => (a.ordinal || 0) - (b.ordinal || 0))
      .map(schema => ({
        fieldName: schema.fieldName,
        displayName: schema.displayName || schema.fieldName,
        dataType: schema.dataType?.toString() || DataSchemaDataType.String
      }));

    const configuredDisplayColumns = this.getConfigValue('displayColumns', []) as string[];
    if (configuredDisplayColumns.length > 0) {
      this.displayedColumns = configuredDisplayColumns.filter(colName =>
        this.columns.some(col => col.fieldName === colName)
      );
    } else {
      this.displayedColumns = this.columns.map(col => col.fieldName);
    }
  }

  private loadRowsFromContext(): void {
    const contextVariable = this.getConfigValue('contextVariable', '');
    if (!contextVariable || !this.viewManager?.contextData) return;

    const contextData = this.viewManager.contextData[contextVariable];
    this.rows = toContextArrayOrEmpty(contextData);
  }

  /** True when this table is bound to an existing context object (no backend row fetch). */
  private get isContextSourced(): boolean {
    return this.getConfigValue<string>('dataSource', '') === 'context';
  }

  getDisplayedColumns(): TableColumn[] {
    return this.displayedColumns
      .map(name => this.columns.find(col => col.fieldName === name))
      .filter((col): col is TableColumn => !!col);
  }

  get contextVariable(): string {
    return this.getConfigValue('contextVariable', '');
  }

  get allowDeleteRows(): boolean {
    return this.getConfigValue('allowDeleteRows', true);
  }

  get addRowButtons(): AddRowButton[] {
    return this.getConfigValue('addRowButtons', []) as AddRowButton[];
  }

  // --- Row Management ---

  onAddRow(button: AddRowButton): void {
    const newRow: any = { __isNew: true };

    // Set all fields to null
    this.columns.forEach(col => {
      newRow[col.fieldName] = null;
    });

    // Apply preset values from the button
    (button.presetValues || []).forEach(preset => {
      if (preset.fieldName && preset.value !== undefined && preset.value !== '') {
        // Check if value references a context variable (format: contextVar.fieldName)
        const contextMatch = preset.value.match(/^(\w+)\.(\w+)$/);
        if (contextMatch && this.viewManager?.contextData) {
          const ctxKey = contextMatch[1];
          const ctxField = contextMatch[2];
          const ctxObj = this.viewManager.contextData[ctxKey];
          if (ctxObj && typeof ctxObj === 'object' && !Array.isArray(ctxObj)) {
            newRow[preset.fieldName] = ctxObj[ctxField] ?? preset.value;
          } else {
            newRow[preset.fieldName] = preset.value;
          }
        } else {
          newRow[preset.fieldName] = preset.value;
        }
      }
    });

    this.rows = [...this.rows, newRow];
    this.emitRows();
  }

  onDeleteRow(rowIndex: number, event: Event): void {
    event.stopPropagation();
    const deletedRow = this.rows[rowIndex];

    // Track existing (non-new) rows for backend deletion
    if (deletedRow && !this.detectIsNewRecord(deletedRow)) {
      this.deletedRows.push(deletedRow);
    }

    this.rows = this.rows.filter((_, i) => i !== rowIndex);
    this.emitRows();
  }

  private detectIsNewRecord(row: any): boolean {
    if (!row || typeof row !== 'object') return false;
    if (row.__isNew === true) return true;
    if (row.__metadata?.isNewRecord === true) return true;
    if (row.__metadata?.isEmptyContext === true) return true;
    return false;
  }

  onCellValueChange(row: any, fieldName: string, value: any): void {
    row[fieldName] = value;
    this.emitRows();
  }

  private registerContextVariable(): void {
    const contextVariable = this.contextVariable;
    if (!contextVariable || !this.viewManager?.contextData) return;

    // Ensure the context variable exists even when the table has no data,
    // so it's discoverable by other cells (e.g., action buttons) in edit mode.
    if (!this.viewManager.contextData.hasOwnProperty(contextVariable)) {
      this.viewManager.contextData[contextVariable] = [];
    }
    if (!this.viewManager.contextData.hasOwnProperty(contextVariable + '__deleted')) {
      this.viewManager.contextData[contextVariable + '__deleted'] = this.deletedRows;
    }
  }

  private emitRows(): void {
    const contextVariable = this.contextVariable;
    if (!contextVariable) return;

    // Update viewManager directly. List contexts are bare arrays, so the rows go
    // straight in — the parent's onContextDataChange overwrites contextData with
    // whatever is emitted, and both sides carry the same shape.
    const emittedValue: any[] = [...this.rows];
    if (this.viewManager?.contextData) {
      this.viewManager.contextData[contextVariable] = emittedValue;
      // Store deleted rows in a companion key so the action button can find them
      this.viewManager.contextData[contextVariable + '__deleted'] = this.deletedRows;
    }

    this.contextDataChange.emit({ [contextVariable]: emittedValue });
  }

  // --- Sorting ---

  onSortColumn(column: TableColumn): void {
    if (this.sortColumn === column.fieldName) {
      if (this.sortDirection === 'asc') {
        this.sortDirection = 'desc';
      } else if (this.sortDirection === 'desc') {
        this.sortColumn = null;
        this.sortDirection = null;
      }
    } else {
      this.sortColumn = column.fieldName;
      this.sortDirection = 'asc';
    }
    this.applySorting();
  }

  private applySorting(): void {
    if (!this.sortColumn || !this.sortDirection) return;
    const field = this.sortColumn;
    const dir = this.sortDirection === 'asc' ? 1 : -1;
    this.rows.sort((a, b) => {
      const valA = a[field];
      const valB = b[field];
      if (valA == null && valB == null) return 0;
      if (valA == null) return 1;
      if (valB == null) return -1;
      if (typeof valA === 'number' && typeof valB === 'number') {
        return (valA - valB) * dir;
      }
      return String(valA).localeCompare(String(valB)) * dir;
    });
  }

  trackByColumn(index: number, column: TableColumn): string {
    return column.fieldName;
  }

  trackByRow(index: number): number {
    return index;
  }

  stringify(value: any): string {
    return value != null ? String(value) : '';
  }

  isNumericColumn(column: TableColumn): boolean {
    const dt = column.dataType.toLowerCase();
    return dt === 'integer' || dt === 'decimal' || dt === 'number' || dt === 'int' || dt === 'float' || dt === 'double';
  }

  isDateColumn(column: TableColumn): boolean {
    const dt = column.dataType.toLowerCase();
    return dt === 'datetime' || dt === 'date';
  }
}
